// SPDX-FileCopyrightText: OpenTalk GmbH <mail@opentalk.eu>
//
// SPDX-License-Identifier: EUPL-1.2
import { Box, StepButton as MuiStepButton, Skeleton, Stack, Step, Stepper, Typography, styled } from '@mui/material';
import { EventId } from '@opentalk/rest-api-rtk-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useLazyGetEventQuery } from '../../../api/rest';
import { EditIcon } from '../../../assets/icons';
import InviteToMeeting from '../../../components/InviteToMeeting';
import { UpdateMeetingForm } from '../../../components/MeetingForms';
import { RequiredFieldsInfo } from '../../../components/RequiredFieldsInfo';
import { useUpdateDocumentTitle } from '../../../hooks/useUpdateDocumentTitle';

const steps = [
  {
    label: 'global-meeting',
    options: {
      count: 1,
    },
  },
  {
    label: 'global-participants',
  },
];

const StepButton = styled(MuiStepButton)(({ theme }) => ({
  padding: 0,
  margin: 0,
  textTransform: 'capitalize',
  '& .MuiStepLabel-label': {
    background: theme.palette.background.main.primary,
    color: theme.palette.background.main.contrastText,
    opacity: 0.9,
  },
  '& .MuiStepLabel-label.Mui-disabled': {
    opacity: 1,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    background: theme.palette.background.main.primary,
    color: theme.palette.background.main.contrastText,
    opacity: 0.9,
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: theme.palette.primary.main,
  },
  '&:not(.Mui-disabled) .MuiStepLabel-iconContainer .MuiSvgIcon-root': {
    background: theme.palette.background.highlightContrast.primary,
  },
}));

const Container = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gridTemplateRows: 'auto auto auto 1fr',
  height: '100%',
  overflow: 'hidden auto',
  gap: theme.spacing(2),
  background: theme.palette.background.main.primary,
  color: theme.palette.background.main.contrastText,
}));

const MAX_INVITEES = 10;

const EditEventsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [getEvent, { data: event, isLoading, error }] = useLazyGetEventQuery();
  const { eventId, formStep } = useParams<'eventId' | 'formStep'>() as { eventId: EventId; formStep: string };
  const eventQuery = useMemo(() => ({ eventId: eventId, inviteesMax: MAX_INVITEES }), [eventId]);
  const pageHeading = t('dashboard-meetings-create-title');
  useUpdateDocumentTitle(pageHeading);

  useEffect(() => {
    getEvent(eventQuery);
  }, [getEvent, eventQuery]);

  useEffect(() => {
    if (!isLoading && !event && error) {
      navigate('/dashboard/meetings/create');
    }
  }, [event, isLoading, navigate, error]);

  useEffect(() => {
    formStep && setActiveStep(parseInt(formStep));
  }, [formStep]);

  const StepperHeader = () => (
    <Stepper activeStep={activeStep}>
      {steps.map(({ label, options }, index) => (
        <Step key={label} disabled={activeStep === index}>
          <StepButton icon={activeStep !== index && <EditIcon />} onClick={() => setActiveStep(index)}>
            {t(label, options)}
          </StepButton>
        </Step>
      ))}
    </Stepper>
  );

  const PlaceholderSkeleton = () => (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Skeleton variant="text" width={450} />
        <Skeleton variant="rectangular" height={40} />
      </Stack>
      <Stack spacing={1}>
        <Skeleton variant="text" width={250} />
        <Skeleton variant="rectangular" height={40} />
      </Stack>
    </Stack>
  );

  if (isLoading) {
    return (
      <Container>
        <StepperHeader />
        <PlaceholderSkeleton />
      </Container>
    );
  }

  return (
    <Container>
      <Typography component="h1">{pageHeading}</Typography>
      <StepperHeader />
      <RequiredFieldsInfo />
      {activeStep === 0 && event && (
        <UpdateMeetingForm existingEvent={event} onForwardButtonClick={() => setActiveStep(1)} />
      )}
      {activeStep === 1 && event && (
        <InviteToMeeting
          isUpdatable={true}
          existingEvent={event}
          onBackButtonClick={() => setActiveStep(0)}
          invitationsSent={() => getEvent(eventQuery)}
        />
      )}
    </Container>
  );
};

export default EditEventsPage;
